import { Component } from 'react';
import { Cell } from '@table-library/react-table-library/table';
import { withTranslation } from 'react-i18next';

import DataTable from '../../../../components/Datatable';
import Loading from '../../../../components/Loading';
import { getSortKeys, setAdditionalStyles } from '../../../../helpers/table';
import { reportsPosition } from '../../GscTools/metrics';
import { formatPeriod } from '../../GscTools/period';
import { MODE } from '../../PagesQueriesChanges/scope';
import { comparisonTableColumns } from '../keys';
import { MODES } from '../scope';
import { policyLabel, topicMatch } from '../topics';

/**
 * Block 02 — every topic the session defined, each against its own baseline.
 *
 * Each row is its own query-filtered request, which is why rows fail separately. The table never
 * gains a total row, for two reasons it states: a query can meet two topics and is then in both
 * rows, and the queries Search Console withholds are in no topic at all — so a sum across topics
 * would be neither a count of distinct queries nor the property's traffic.
 *
 * The modes re-order the rows already held, the same three orders the rows of Pages, Queries &
 * Changes use. Growing and Decaying are movements in the metric, not a verdict on the content: a
 * topic can decline because demand fell, because a query moved to another topic's wording, or
 * because Search Console withheld more of it, and nothing here can tell those apart.
 *
 * Picking a topic is what blocks 03 and 04 are about. The pick is stated three times over — the
 * row's button reads Showing, the row stays marked, and the chip beside the modes names the topic
 * with its rule and offers the way back out.
 */
class TopicComparison extends Component {
    constructor(props) {
        super(props);
        this.additionalBox = this.additionalBox.bind(this);
        this.actionColumn = this.actionColumn.bind(this);
        this.handleRowClick = this.handleRowClick.bind(this);
    }

    /** Clicking the row does what its button does, including toggling back off. */
    handleRowClick(item) {
        const { activeTopic, onSelect } = this.props;
        onSelect(item.key === activeTopic ? null : item.key);
    }

    /** The button that loads a topic's trend and contributors. Left out of the exports. */
    actionColumn() {
        const { t, activeTopic, onSelect } = this.props;

        return {
            label: t('tools.topic_clusters.comparison.details'),
            key: 'details',
            data: 'key',
            sortType: (array) => array,
            cellSize: 'minmax(140px, 160px)',
            Component: Cell,
            renderCell: ({ item, csvDownload }) => {
                if (csvDownload) return '';

                const picked = item.key === activeTopic;

                return (
                    <div className="d-flex">
                        <button
                            type="button"
                            className={`table-action-main-btn ${picked ? 'is-picked' : ''}`}
                            aria-pressed={picked}
                            onClick={() => onSelect(picked ? null : item.key)}
                        >
                            {picked && <i className="icon-check-icon" aria-hidden="true" />}
                            {picked
                                ? t('tools.topic_clusters.comparison.showing')
                                : t('tools.topic_clusters.comparison.show')}
                        </button>
                    </div>
                );
            },
        };
    }

    /** Popular / Growing / Decaying, drawn as the table-filter control the Categories table uses. */
    additionalBox() {
        const { t, scope, activeTopic, onMode, onSelect } = this.props;
        const topic = scope.topics.find((item) => item.name === activeTopic);
        const chip = topic
            ? t('tools.topic_clusters.comparison.active', {
                  topic: topic.name,
                  rule: `${policyLabel(t, topic.policy)} · ${topicMatch(topic)}`,
              })
            : null;

        return (
            <div className="table-additional-view">
                <div className="table-additional-view-btns">
                    <div className="table-additional-view-btn-groups">
                        {MODES.map((mode) => (
                            <button
                                type="button"
                                key={mode}
                                className={scope.mode === mode ? 'active' : ''}
                                aria-pressed={scope.mode === mode}
                                onClick={() => onMode(mode)}
                            >
                                {t(`tools.topic_clusters.modes.${mode}`)}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="gsc-tool-rows-meta">
                    <span className="gsc-tool-sub">
                        {t('tools.topic_clusters.comparison.ordered_by', {
                            metric: t(`tools.gsc.metrics.${scope.metric}_short`),
                        })}
                    </span>
                    {!!topic && (
                        <span className="gsc-tool-chip" title={chip}>
                            <span>{chip}</span>
                            <button
                                type="button"
                                aria-label={t('tools.topic_clusters.comparison.clear', {
                                    topic: topic.name,
                                })}
                                onClick={() => onSelect(null)}
                            >
                                <i className="icon-x-close" />
                            </button>
                        </span>
                    )}
                </div>
            </div>
        );
    }

    render() {
        const { t, scope, totals, rows, activeTopic, onExport } = this.props;

        const columns = comparisonTableColumns(t, {
            hasPosition: reportsPosition(scope.searchType),
        });
        const tableColumns = [...columns, this.actionColumn()];

        const loading = scope.topics.some((topic) =>
            ['idle', 'loading'].includes(totals[topic.name]?.status),
        );
        const failed = scope.topics.filter((topic) => totals[topic.name]?.status === 'error');
        const noComparison = !scope.baseline;

        return (
            <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
                <div className="card-header d-flex justify-content-between">
                    <div>
                        <p className="card-title">
                            02 · {t('tools.topic_clusters.comparison.title')}
                        </p>
                        <p className="gsc-tool-sub">
                            {t('tools.topic_clusters.comparison.description')} ·{' '}
                            {formatPeriod(scope.current)}
                            {!noComparison && <> ↔ {formatPeriod(scope.baseline)}</>}
                        </p>
                        <p className="gsc-tool-rows-hint">
                            <i className="icon-info" aria-hidden="true" />
                            {t('tools.topic_clusters.comparison.select_hint')}
                        </p>
                    </div>

                    <div className="gsc-tool-icon-actions">
                        <button
                            type="button"
                            title={t('tools.topic_clusters.comparison.export')}
                            aria-label={t('tools.topic_clusters.comparison.export')}
                            onClick={onExport}
                            disabled={loading || !rows.length}
                        >
                            <i className="icon-fi_download" />
                        </button>
                    </div>
                </div>

                <div className="card-body">
                    {noComparison && scope.mode !== MODE.POPULAR && (
                        <div className="gsc-tool-banner">
                            {t('tools.topic_clusters.comparison.no_comparison')}
                        </div>
                    )}

                    {loading && <Loading padding={20} />}

                    {/* One banner per failed topic, naming it: the rest of the table is still
                        showing numbers. */}
                    {!loading &&
                        failed.map((topic) => (
                            <div key={topic.name} className="gsc-tool-banner error">
                                {t('tools.topic_clusters.comparison.topic_failed', {
                                    topic: topic.name,
                                    reason:
                                        totals[topic.name]?.error ??
                                        t('global.something_went_wrong'),
                                })}
                            </div>
                        ))}

                    {!loading && failed.length < scope.topics.length && (
                        <>
                            <DataTable
                                nodes={rows}
                                COLUMNS={tableColumns}
                                customColumnsForExport={columns}
                                sortFns={getSortKeys(tableColumns)}
                                additionalStyles={setAdditionalStyles(tableColumns)}
                                additionalBox={this.additionalBox}
                                fileName={t('tools.topic_clusters.comparison.title')}
                                customEmptyMessage={t(
                                    `tools.topic_clusters.comparison.empty.${scope.mode}`,
                                )}
                                customContainerStyles="gsc-tool-key-table"
                                rowClassName={(item) =>
                                    item.key === activeTopic ? 'dt-row-picked' : ''
                                }
                                onRowClick={this.handleRowClick}
                            />

                            <p className="gsc-tool-table-note">
                                {t('tools.topic_clusters.comparison.no_total')}
                            </p>
                            <p className="gsc-tool-table-note">
                                {t('tools.topic_clusters.comparison.mode_note')}
                            </p>
                        </>
                    )}
                </div>
            </div>
        );
    }
}

export default withTranslation()(TopicComparison);
